import React, { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { STEP_LABELS, WEBINAR_API } from "../../constants";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useSessionForm } from "../../hooks/useSessionForm";
import { useSessionMutations } from "../../hooks/useSessionMutations";
import { getSessionActions } from "../../status/getSessionActions";
import { SessionMode } from "../../types/actions";
import { WebinarSession } from "../../types/models";
import AudienceStep from "../SessionWizard/steps/AudienceStep";
import BasicsStep from "../SessionWizard/steps/BasicsStep";
import ContentStep from "../SessionWizard/steps/ContentStep";
import ScheduleStep from "../SessionWizard/steps/ScheduleStep";
import PublishChecklist from "../PublishChecklist/PublishChecklist";
import SessionCardPreview from "../SessionCardPreview/SessionCardPreview";
import "./SessionDrawer.scss";

export type { SessionDrawerState } from "../../types/actions";

type Props = {
  open: boolean;
  mode: SessionMode;
  /** Clicked list row for edit/duplicate. */
  session?: WebinarSession | null;
  /** Current list (host clash) — already loaded on landing. */
  existingSessions?: WebinarSession[];
  onClose: () => void;
  onSaved: () => void;
};

const SessionDrawer: React.FC<Props> = ({
  open,
  mode,
  session = null,
  existingSessions = [],
  onClose,
  onSaved,
}) => {
  const formApi = useSessionForm({
    open,
    mode,
    initialSession: session,
    existingSessions,
    onSuccess: (_session, action) => {
      onSaved();
      if (action === "draft") {
        // Stay open so coaches can continue steps after POST/PATCH.
        return;
      }
      toast.success(
        mode === "edit"
          ? "Saved · copy updates silently"
          : "Published · discovery notification queued"
      );
      onClose();
    },
  });

  const { deleteDraft } = useSessionMutations({
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    if (formApi.bootError) {
      toast.error(formApi.bootError);
      onClose();
    }
  }, [open, formApi.bootError, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") tryClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formApi.dirty]);

  const heading = useMemo(() => {
    if (mode === "edit") return "Edit session";
    return "New session";
  }, [mode]);

  const stateNote = useMemo(() => {
    if (mode === "edit") {
      return formApi.form.currentStatus === "scheduled"
        ? "Published — copy edits go out silently, time changes notify members"
        : "Draft — not visible to members";
    }
    if (mode === "duplicate") {
      return "Copied everything except the date — pick a new slot";
    }
    return "Draft — save at any point and come back";
  }, [mode, formApi.form.currentStatus]);

  const canDeleteDraft =
    mode === "edit" &&
    formApi.form.currentStatus === "draft" &&
    getSessionActions({ status: "draft" }).canDeleteDraft;

  const discardLabel =
    canDeleteDraft
      ? "Delete session"
      : mode === "edit" && formApi.form.currentStatus === "scheduled"
      ? "Close"
      : "Discard draft";

  const selectedHost = formApi.hosts.find((h) => h.id === formApi.form.hostId);
  const selectedTopic = formApi.topics.find(
    (t) => t.id === formApi.form.topicId
  );

  const tryClose = () => {
    if (formApi.dirty) {
      const ok = window.confirm(
        "You have unsaved changes. Leave without saving?"
      );
      if (!ok) return;
    }
    onClose();
  };

  const handleDiscard = async () => {
    if (canDeleteDraft && formApi.form.id && WEBINAR_API.canDeleteDraft) {
      const ok = window.confirm("Delete this draft? This cannot be undone.");
      if (!ok) return;
      await deleteDraft({
        id: formApi.form.id,
        status: "draft",
      });
      return;
    }
    if (mode === "edit" && formApi.form.currentStatus === "scheduled") {
      tryClose();
      return;
    }
    if (formApi.dirty) {
      const ok = window.confirm("Discard this draft?");
      if (!ok) return;
    }
    toast.info(mode === "edit" ? "Closed" : "Draft discarded");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="wf-drawer" role="dialog" aria-modal="true" aria-label={heading}>
      <button
        type="button"
        className="wf-drawer__backdrop"
        aria-label="Close panel"
        onClick={tryClose}
      />
      <div className="wf-drawer__panel">
        <header className="wf-drawer__header">
          <div className="wf-drawer__header-top">
            <div>
              <h2>{heading}</h2>
              <div className="wf-drawer__meta">
                <span className="saved">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Saves on Next
                </span>
                <span className="dot" />
                <span className="note">{stateNote}</span>
              </div>
            </div>
            <button
              type="button"
              className="wf-drawer__close"
              aria-label="Close"
              onClick={tryClose}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="wf-drawer__steps" role="tablist" aria-label="Form steps">
            {STEP_LABELS.map((label, i) => {
              const active = formApi.step === i;
              return (
                <button
                  key={label}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`wf-drawer__step${active ? " is-active" : ""}`}
                  onClick={() => formApi.setStep(i)}
                  disabled={formApi.loading}
                >
                  <span className="num">{i + 1}</span>
                  <span className="label">{label}</span>
                </button>
              );
            })}
          </div>
        </header>

        <div className="wf-drawer__body">
          {formApi.loading ? (
            <div className="wf-drawer__loading">Loading…</div>
          ) : (
            <>
              <div className="wf-drawer__form gf-s">
                <div id="wf-drawer-scroll-top" aria-hidden="true" />
                {formApi.step === 0 && (
                  <BasicsStep
                    form={formApi.form}
                    hosts={formApi.hosts}
                    topics={formApi.topics}
                    setField={formApi.setField}
                    onHostsChange={formApi.setHosts}
                    onTopicsChange={formApi.setTopics}
                  />
                )}
                {formApi.step === 1 && (
                  <ContentStep form={formApi.form} setField={formApi.setField} />
                )}
                {formApi.step === 2 && (
                  <AudienceStep form={formApi.form} setField={formApi.setField} />
                )}
                {formApi.step === 3 && (
                  <ScheduleStep
                    form={formApi.form}
                    setField={formApi.setField}
                    hostClash={formApi.hostClash}
                  />
                )}
                {formApi.error ? (
                  <div className="wf-drawer__error">{formApi.error}</div>
                ) : null}
              </div>
              <aside className="wf-drawer__aside gf-s">
                <SessionCardPreview
                  form={formApi.form}
                  host={selectedHost}
                  topic={selectedTopic}
                />
                <PublishChecklist items={formApi.publishValidation.checklist} />
              </aside>
            </>
          )}
        </div>

        <footer className="wf-drawer__footer">
          <button
            type="button"
            className="wf-drawer__discard"
            onClick={handleDiscard}
            disabled={formApi.saving || formApi.loading}
          >
            {discardLabel}
          </button>
          <div className="wf-drawer__footer-right">
            {formApi.footerFlags.showDraft ? (
              <button
                type="button"
                className="wf-btn ghost"
                onClick={() => formApi.submit("draft")}
                disabled={formApi.saving || formApi.loading}
              >
                {formApi.saving ? "Saving…" : "Save as draft"}
              </button>
            ) : null}
            {formApi.step > 0 ? (
              <button
                type="button"
                className="wf-btn ghost"
                onClick={() => formApi.setStep(formApi.step - 1)}
                disabled={formApi.saving || formApi.loading}
              >
                Back
              </button>
            ) : null}
            {formApi.step < 3 ? (
              <button
                type="button"
                className="wf-btn dark"
                onClick={() => formApi.goNext()}
                disabled={formApi.saving || formApi.loading}
              >
                Next · {STEP_LABELS[Math.min(3, formApi.step + 1)]}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            ) : null}
            {formApi.footerFlags.showPublish && formApi.step === 3 ? (
              <button
                type="button"
                className={`wf-btn primary wf-drawer__publish${
                  formApi.publishValidation.isValid ? "" : " is-disabled"
                }`}
                onClick={() => formApi.submit("publish")}
                disabled={
                  formApi.saving ||
                  formApi.loading ||
                  !formApi.publishValidation.isValid
                }
                title={
                  !WEBINAR_API.canPublish &&
                  formApi.form.currentStatus !== "scheduled"
                    ? "Publishing is not available yet"
                    : undefined
                }
              >
                {formApi.saving
                  ? "Saving…"
                  : formApi.footerFlags.publishLabel}
              </button>
            ) : null}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SessionDrawer;
